import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format, addDays, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import FloatingContact from '../../components/public/FloatingContact';
import { publicGet, publicPost } from '../../lib/publicApi';
import {
  ArrowLeft, ArrowRight, Calendar as CalIcon, Check,
  Loader2, Moon, Sun, Sparkles, User, Mail, Phone, MessageCircle,
  Banknote, CreditCard, Smartphone, Shield, Star, ChevronDown,
} from 'lucide-react';

const STEPS = ['Votre séjour', 'Coordonnées', 'Paiement'];

const Stepper = ({ step }) => (
  <div className="flex items-center justify-center gap-3 mb-12" data-testid="stepper">
    {STEPS.map((label, i) => {
      const idx = i + 1;
      const active = step === idx;
      const done = step > idx;
      return (
        <div key={label} className="flex items-center gap-3">
          <div className={`flex items-center gap-2 ${active ? 'text-[#D4AF37]' : done ? 'text-[#D4AF37]/70' : 'text-[#5C5851]'}`}>
            <div className={`w-7 h-7 flex items-center justify-center border text-xs font-body font-medium tabular-nums transition-colors ${active ? 'border-[#D4AF37] bg-[#D4AF37] text-[#0D0C0B]' : done ? 'border-[#D4AF37]/60 text-[#D4AF37]' : 'border-[#3A3833]'}`}>
              {done ? <Check className="w-3.5 h-3.5" /> : idx}
            </div>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.22em] font-body">{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`w-8 sm:w-14 h-px ${done ? 'bg-[#D4AF37]/40' : 'bg-[#3A3833]'}`} />}
        </div>
      );
    })}
  </div>
);

export default function ReservationPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [property, setProperty] = useState(null);
  const [config, setConfig] = useState(null);
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);

  const [sessionType, setSessionType] = useState(searchParams.get('session') || 'night');
  const [checkIn, setCheckIn] = useState(searchParams.get('checkin') || '');
  const [checkOut, setCheckOut] = useState(searchParams.get('checkout') || '');
  const [guestsCount, setGuestsCount] = useState(parseInt(searchParams.get('guests')) || 2);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [promo, setPromo] = useState('');
  const [pricing, setPricing] = useState(null);
  const [checking, setChecking] = useState(false);
  const [optionsExpanded, setOptionsExpanded] = useState(false);

  const [guest, setGuest] = useState({ name: '', email: '', phone: '', message: '' });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [submitting, setSubmitting] = useState(false);

  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalRendered, setPaypalRendered] = useState(false);

  useEffect(() => {
    publicGet(`/api/property/${propertyId}/public`)
      .then(setProperty)
      .catch(() => toast.error('Propriété introuvable'))
      .finally(() => setLoading(false));
    publicGet('/api/public/site-config').then(setConfig).catch(() => {});
    publicGet('/api/public/paypal/config').then(setPaypalConfig).catch(() => {});
  }, [propertyId]);

  // Auto-set checkout = checkin + 1 for night
  useEffect(() => {
    if (sessionType === 'night' && checkIn && (!checkOut || checkOut <= checkIn)) {
      setCheckOut(format(addDays(parseISO(checkIn), 1), 'yyyy-MM-dd'));
    }
    if (sessionType === 'dayuse' && checkIn) {
      setCheckOut(checkIn);
    }
  }, [sessionType, checkIn]); // eslint-disable-line

  // Persist form to localStorage (so users don't lose data on refresh)
  useEffect(() => {
    const saved = localStorage.getItem(`era_lodge_booking_${propertyId}`);
    if (saved) {
      try {
        const d = JSON.parse(saved);
        if (d.guest) setGuest(d.guest);
      } catch {}
    }
  }, [propertyId]);
  useEffect(() => {
    if (guest.name || guest.email) {
      localStorage.setItem(`era_lodge_booking_${propertyId}`, JSON.stringify({ guest }));
    }
  }, [guest, propertyId]);

  const enabledOptions = useMemo(
    () => (property?.options || []).filter(o => o.enabled !== false),
    [property]
  );

  const selectedIds = Object.keys(selectedOptions).filter(k => selectedOptions[k]);
  const selectedOptionsCount = selectedIds.length;

  const fetchPricing = async () => {
    if (!checkIn) return;
    const inDate = checkIn;
    const outDate = sessionType === 'dayuse' ? checkIn : checkOut;
    if (!outDate) return;
    setChecking(true);
    try {
      const params = new URLSearchParams({
        check_in: inDate, check_out: outDate, session_type: sessionType,
      });
      if (selectedIds.length) params.set('selected_option_ids', selectedIds.join(','));
      if (promo) params.set('promo_code', promo);
      const data = await publicGet(`/api/guest/property/${propertyId}/availability?${params.toString()}`);
      setPricing(data);
      return data;
    } catch (e) {
      toast.error('Impossible de calculer le tarif');
    } finally {
      setChecking(false);
    }
  };

  // Recompute pricing whenever options/promo/dates change
  useEffect(() => {
    if (checkIn) fetchPricing();
    // eslint-disable-next-line
  }, [selectedOptions, promo, sessionType, checkOut, checkIn]);

  // Auto-skip to step 2 if URL params provided everything (quick-book widget)
  useEffect(() => {
    if (searchParams.get('skip') === '1' && checkIn && property && step === 1) {
      // Verify availability automatically
      fetchPricing().then(data => {
        if (data?.available) setStep(2);
      });
    }
    // eslint-disable-next-line
  }, [property]);

  const goNextFromStay = async () => {
    if (!checkIn || (sessionType === 'night' && !checkOut)) {
      toast.error('Choisissez vos dates');
      return;
    }
    const data = await fetchPricing();
    if (!data) return;
    if (!data.available) {
      toast.error('Ces dates ne sont pas disponibles');
      return;
    }
    setStep(2);
  };

  const goNextFromInfo = () => {
    if (!guest.name || !guest.email || !guest.phone) {
      toast.error('Merci de remplir vos coordonnées');
      return;
    }
    setStep(3);
  };

  const submitBooking = async (overrides = {}) => {
    setSubmitting(true);
    try {
      const payload = {
        property_id: propertyId,
        guest_name: guest.name,
        guest_email: guest.email,
        guest_phone: guest.phone,
        message: guest.message,
        check_in: checkIn,
        check_out: sessionType === 'dayuse' ? checkIn : checkOut,
        session_type: sessionType,
        guests_count: guestsCount,
        selected_option_ids: selectedIds,
        promo_code: promo || '',
        payment_method: paymentMethod,
        total_price: pricing?.total_price || 0,
        ...overrides,
      };
      const data = await publicPost('/api/guest/bookings', payload);
      return data;
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la réservation');
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    const data = await submitBooking();
    if (data?.booking_id) {
      localStorage.removeItem(`era_lodge_booking_${propertyId}`);
      navigate(`/confirmation/${data.booking_id}`);
    }
  };

  // PayPal SDK
  useEffect(() => {
    if (paymentMethod !== 'paypal') return;
    if (!paypalConfig?.client_id) return;
    if (window.paypal) { setPaypalReady(true); return; }
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${paypalConfig.client_id}&currency=${paypalConfig.currency || 'EUR'}&intent=capture&disable-funding=credit,card`;
    script.async = true;
    script.onload = () => setPaypalReady(true);
    script.onerror = () => toast.error('Échec du chargement de PayPal');
    document.body.appendChild(script);
  }, [paymentMethod, paypalConfig]);

  useEffect(() => {
    if (paymentMethod !== 'paypal' || step !== 3 || !paypalReady || paypalRendered) return;
    if (!window.paypal) return;
    const container = document.getElementById('paypal-button-container');
    if (!container) return;
    container.innerHTML = '';
    setPaypalRendered(true);
    window.paypal.Buttons({
      style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
      createOrder: async () => {
        const booking = await submitBooking({ payment_method: 'paypal' });
        if (!booking?.booking_id) throw new Error('booking_failed');
        window.__lastBookingId = booking.booking_id;
        const data = await publicPost(`/api/guest/paypal/create-order?booking_id=${booking.booking_id}&amount=${pricing.total_price}`);
        return data.order_id;
      },
      onApprove: async (data) => {
        const bookingId = window.__lastBookingId;
        await publicPost(`/api/guest/paypal/capture-order?booking_id=${bookingId}&paypal_order_id=${data.orderID}`);
        toast.success('Paiement confirmé');
        localStorage.removeItem(`era_lodge_booking_${propertyId}`);
        navigate(`/confirmation/${bookingId}`);
      },
      onError: () => { toast.error('Paiement PayPal impossible. Veuillez réessayer.'); },
    }).render('#paypal-button-container');
  }, [paypalReady, paymentMethod, step]); // eslint-disable-line

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0C0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }
  if (!property) {
    return (
      <div className="min-h-screen bg-[#0D0C0B] flex flex-col items-center justify-center p-6 text-center">
        <p className="overline mb-4">404</p>
        <h1 className="font-display text-5xl mb-6">Propriété introuvable</h1>
        <Link to="/" className="btn-luxury-outline">Retour à l'accueil</Link>
      </div>
    );
  }

  const minDate = format(new Date(), 'yyyy-MM-dd');
  const minOut = checkIn ? format(addDays(parseISO(checkIn), 1), 'yyyy-MM-dd') : minDate;

  return (
    <div className="min-h-screen bg-[#0D0C0B] text-[#F5F2EB] pb-24 lg:pb-0" data-testid="reservation-page">
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#A39E93] hover:text-[#D4AF37] transition-colors text-xs uppercase tracking-[0.22em] font-body" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" /> Era Lodge
          </Link>
          <p className="font-display text-lg italic text-[#D4AF37] hidden sm:block">{property.name}</p>
          <p className="text-xs uppercase tracking-[0.22em] text-[#A39E93] font-body hidden md:block">Réservation directe</p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16 grid lg:grid-cols-12 gap-10 md:gap-16">
        <div className="lg:col-span-7">
          <Stepper step={step} />

          {/* STEP 1 — Stay (Session + Dates + Guests) */}
          {step === 1 && (
            <div className="animate-fade-in" data-testid="step-stay">
              <p className="overline mb-3">Étape 1 sur 3</p>
              <h2 className="font-display text-4xl md:text-5xl font-light mb-3">Votre <span className="italic text-[#D4AF37]">séjour</span></h2>
              <p className="font-body font-light text-[#A39E93] mb-10">Choisissez session, dates et nombre de voyageurs.</p>

              {/* Session toggle */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  { key: 'night', icon: Moon, title: 'Session Nuit', sub: `${property.night_check_in_hour || '18:00'} → ${property.night_check_out_hour || '10:00'}`, price: property.night_price ?? property.base_price ?? 100, enabled: property.night_enabled !== false },
                  { key: 'dayuse', icon: Sun, title: 'Day Use', sub: `${property.dayuse_start_hour || '12:00'} → ${property.dayuse_end_hour || '15:00'}`, price: property.dayuse_price ?? 120, enabled: property.dayuse_enabled !== false },
                ].filter(s => s.enabled).map((s) => {
                  const Icon = s.icon;
                  const active = sessionType === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setSessionType(s.key)}
                      className={`text-left p-5 border transition-all duration-300 ${active ? 'border-[#D4AF37] bg-[#D4AF37]/[0.04] gold-glow' : 'border-white/10 hover:border-[#D4AF37]/40'}`}
                      data-testid={`session-${s.key}-btn`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.3} />
                        <p className="font-display text-lg text-[#D4AF37]">€{s.price}</p>
                      </div>
                      <h3 className="font-display text-2xl mb-1">{s.title}</h3>
                      <p className="overline">{s.sub}</p>
                    </button>
                  );
                })}
              </div>

              {/* Dates */}
              <div className={`grid ${sessionType === 'night' ? 'sm:grid-cols-2' : 'sm:grid-cols-1'} gap-4 mb-6`}>
                <div className="space-y-2">
                  <label className="overline">{sessionType === 'night' ? 'Arrivée' : 'Date du Day Use'}</label>
                  <div className="relative">
                    <CalIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                    <input
                      type="date" min={minDate} value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      className="w-full bg-[#141312] border border-white/10 focus:border-[#D4AF37] py-4 pl-12 pr-4 font-body text-base text-[#F5F2EB] outline-none transition-colors"
                      data-testid="check-in-input"
                    />
                  </div>
                </div>
                {sessionType === 'night' && (
                  <div className="space-y-2">
                    <label className="overline">Départ</label>
                    <div className="relative">
                      <CalIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      <input
                        type="date" min={minOut} value={checkOut}
                        onChange={(e) => setCheckOut(e.target.value)}
                        className="w-full bg-[#141312] border border-white/10 focus:border-[#D4AF37] py-4 pl-12 pr-4 font-body text-base text-[#F5F2EB] outline-none transition-colors"
                        data-testid="check-out-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Guests */}
              <div className="mb-10">
                <label className="overline block mb-3">Voyageurs</label>
                <div className="flex items-center gap-3">
                  {[1, 2, 3, 4].slice(0, property.max_guests || 2).map(n => (
                    <button
                      key={n} onClick={() => setGuestsCount(n)}
                      className={`w-12 h-12 border font-body text-base tabular-nums transition-colors ${guestsCount === n ? 'border-[#D4AF37] bg-[#D4AF37] text-[#0D0C0B]' : 'border-white/10 hover:border-[#D4AF37]/40 text-[#F5F2EB]'}`}
                      data-testid={`guests-${n}-btn`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={goNextFromStay} disabled={checking} className="btn-luxury" data-testid="next-to-info-btn">
                  {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continuer <ArrowRight className="ml-3 w-4 h-4" /></>}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Contact + Rituels (collapsible) + Promo */}
          {step === 2 && (
            <div className="animate-fade-in" data-testid="step-info">
              <p className="overline mb-3">Étape 2 sur 3</p>
              <h2 className="font-display text-4xl md:text-5xl font-light mb-3">Vos <span className="italic text-[#D4AF37]">coordonnées</span></h2>
              <p className="font-body font-light text-[#A39E93] mb-10">Pour confirmer votre réservation et préparer votre arrivée.</p>

              <div className="space-y-4 mb-10">
                {[
                  { key: 'name', label: 'Nom complet', type: 'text', icon: User, ph: 'Marie Dupont' },
                  { key: 'email', label: 'Email', type: 'email', icon: Mail, ph: 'marie@email.com' },
                  { key: 'phone', label: 'Téléphone', type: 'tel', icon: Phone, ph: '+33 6 12 34 56 78' },
                ].map(f => {
                  const Icon = f.icon;
                  return (
                    <div key={f.key} className="space-y-2">
                      <label className="overline">{f.label}</label>
                      <div className="relative">
                        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                        <input
                          type={f.type} placeholder={f.ph} value={guest[f.key]}
                          onChange={(e) => setGuest(g => ({ ...g, [f.key]: e.target.value }))}
                          className="w-full bg-[#141312] border border-white/10 focus:border-[#D4AF37] py-4 pl-12 pr-4 font-body text-base text-[#F5F2EB] outline-none transition-colors placeholder-[#5C5851]"
                          data-testid={`guest-${f.key}-input`}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="space-y-2">
                  <label className="overline">Message (optionnel)</label>
                  <div className="relative">
                    <MessageCircle className="absolute left-4 top-4 w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                    <textarea
                      rows={2} placeholder="Une attention particulière, une occasion à célébrer..."
                      value={guest.message}
                      onChange={(e) => setGuest(g => ({ ...g, message: e.target.value }))}
                      className="w-full bg-[#141312] border border-white/10 focus:border-[#D4AF37] py-4 pl-12 pr-4 font-body text-base text-[#F5F2EB] outline-none transition-colors placeholder-[#5C5851]"
                      data-testid="guest-message-input"
                    />
                  </div>
                </div>
              </div>

              {/* Rituels — collapsible (so it doesn't feel mandatory) */}
              {enabledOptions.length > 0 && (
                <div className="border border-white/10 mb-6" data-testid="rituals-accordion">
                  <button
                    onClick={() => setOptionsExpanded(e => !e)}
                    className="w-full px-6 py-5 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
                    data-testid="rituals-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      <p className="font-display text-xl">Ajouter des rituels <span className="text-[#A39E93] font-body text-sm">(optionnel)</span></p>
                    </div>
                    <div className="flex items-center gap-3">
                      {selectedOptionsCount > 0 && (
                        <span className="text-xs text-[#D4AF37] font-body font-medium">{selectedOptionsCount} sélectionné{selectedOptionsCount > 1 ? 's' : ''}</span>
                      )}
                      <ChevronDown className={`w-4 h-4 text-[#A39E93] transition-transform ${optionsExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {optionsExpanded && (
                    <div className="border-t border-white/10 p-6 grid sm:grid-cols-2 gap-3">
                      {enabledOptions.map((opt) => {
                        const active = !!selectedOptions[opt.id];
                        return (
                          <button
                            key={opt.id}
                            onClick={() => setSelectedOptions(prev => ({ ...prev, [opt.id]: !prev[opt.id] }))}
                            className={`text-left p-4 border transition-colors ${active ? 'border-[#D4AF37] bg-[#D4AF37]/[0.04]' : 'border-white/10 hover:border-[#D4AF37]/40'}`}
                            data-testid={`option-toggle-${opt.id}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="font-display text-base">{opt.name}</p>
                              <p className="text-xs text-[#D4AF37] tabular-nums whitespace-nowrap">{opt.price > 0 ? `+€${opt.price}` : 'inclus'}</p>
                            </div>
                            <p className="text-xs text-[#A39E93] font-body font-light">{opt.description}</p>
                            {active && <p className="mt-2 text-[10px] text-[#D4AF37] uppercase tracking-[0.22em] flex items-center gap-1"><Check className="w-3 h-3" /> Ajouté</p>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Promo */}
              <div className="mb-10">
                <label className="overline block mb-2">Code promo (optionnel)</label>
                <input
                  type="text" placeholder="WELLNESS10" value={promo}
                  onChange={(e) => setPromo(e.target.value)}
                  className="w-full bg-[#141312] border border-white/10 focus:border-[#D4AF37] py-4 px-4 font-body text-base text-[#F5F2EB] outline-none transition-colors placeholder-[#5C5851] uppercase tracking-widest"
                  data-testid="promo-input"
                />
              </div>

              <div className="flex justify-between items-center">
                <button onClick={() => setStep(1)} className="text-xs uppercase tracking-[0.22em] text-[#A39E93] hover:text-[#D4AF37] flex items-center gap-2 font-body" data-testid="back-to-stay-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> Retour
                </button>
                <button onClick={goNextFromInfo} className="btn-luxury" data-testid="next-to-payment-btn">
                  Récapitulatif <ArrowRight className="ml-3 w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — Payment */}
          {step === 3 && (
            <div className="animate-fade-in" data-testid="step-payment">
              <p className="overline mb-3">Étape 3 sur 3</p>
              <h2 className="font-display text-4xl md:text-5xl font-light mb-3">Mode de <span className="italic text-[#D4AF37]">paiement</span></h2>
              <p className="font-body font-light text-[#A39E93] mb-10">Votre instant est à un clic.</p>

              <div className="space-y-3">
                {[
                  { key: 'cash', icon: Banknote, title: 'Espèces sur place', sub: 'Réglez à votre arrivée, sans frais.' },
                  { key: 'wero', icon: Smartphone, title: 'Wero / Paylib', sub: 'Virement instantané par téléphone.' },
                  { key: 'paypal', icon: CreditCard, title: 'PayPal', sub: 'Paiement sécurisé, carte ou compte PayPal.' },
                ].map(p => {
                  const Icon = p.icon;
                  const active = paymentMethod === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => { setPaymentMethod(p.key); setPaypalRendered(false); }}
                      className={`w-full text-left p-5 border flex items-center gap-4 transition-colors ${active ? 'border-[#D4AF37] bg-[#D4AF37]/[0.04]' : 'border-white/10 hover:border-[#D4AF37]/40'}`}
                      data-testid={`payment-${p.key}-btn`}
                    >
                      <div className={`w-12 h-12 flex items-center justify-center border ${active ? 'border-[#D4AF37] bg-[#D4AF37] text-[#0D0C0B]' : 'border-white/10 text-[#D4AF37]'}`}>
                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <p className="font-display text-lg">{p.title}</p>
                        <p className="font-body font-light text-xs text-[#A39E93]">{p.sub}</p>
                      </div>
                      {active && <Check className="w-5 h-5 text-[#D4AF37]" />}
                    </button>
                  );
                })}
              </div>

              {paymentMethod === 'wero' && (
                <div className="mt-6 p-5 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04]" data-testid="wero-info">
                  <p className="overline mb-2">Comment payer en Wero</p>
                  <p className="font-body font-light text-sm text-[#A39E93] mb-3">Envoyez €{pricing?.total_price?.toFixed(2)} à l'un des numéros :</p>
                  <ul className="space-y-1 mb-3">
                    {(config?.wero_phones || ['0605641364', '0634515985']).map(p => (
                      <li key={p} className="font-display text-xl text-[#F5F2EB] tabular-nums">{p}</li>
                    ))}
                  </ul>
                  <p className="text-[11px] font-body text-[#A39E93]/80">Indiquez votre nom en référence.</p>
                </div>
              )}

              {paymentMethod === 'paypal' && (
                <div className="mt-6 p-5 border border-white/10" data-testid="paypal-section">
                  <p className="overline mb-3">Cliquez pour payer</p>
                  {!paypalReady && <p className="text-sm text-[#A39E93] font-body">Chargement de PayPal…</p>}
                  <div id="paypal-button-container" />
                </div>
              )}

              <div className="flex justify-between items-center mt-10">
                <button onClick={() => setStep(2)} className="text-xs uppercase tracking-[0.22em] text-[#A39E93] hover:text-[#D4AF37] flex items-center gap-2 font-body" data-testid="back-to-info-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> Retour
                </button>
                {paymentMethod !== 'paypal' && (
                  <button onClick={handleConfirm} disabled={submitting} className="btn-luxury" data-testid="confirm-booking-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirmer ma réservation <Check className="ml-3 w-4 h-4" /></>}
                  </button>
                )}
              </div>

              <p className="mt-8 flex items-center gap-2 text-xs text-[#A39E93]/70 font-body">
                <Shield className="w-3.5 h-3.5" /> Paiement sécurisé · Confirmation immédiate
              </p>
            </div>
          )}
        </div>

        {/* Right Summary */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="border border-white/10 overflow-hidden" data-testid="summary-card">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={property.hero_image_url || property.image_url || 'https://images.unsplash.com/photo-1774175927628-40e82ee8fe13?w=1200&q=80'} alt={property.name} className="w-full h-full object-cover ken-burns" />
              </div>
              <div className="p-6 md:p-8">
                <p className="overline mb-2">Votre suite</p>
                <h3 className="font-display text-3xl mb-2">{property.name}</h3>
                <p className="font-body font-light text-sm text-[#A39E93] mb-6">{property.address}</p>

                <div className="luxury-divider mb-6" />

                <ul className="space-y-3 text-sm font-body font-light">
                  <li className="flex justify-between">
                    <span className="text-[#A39E93] flex items-center gap-2">
                      {sessionType === 'night' ? <Moon className="w-3.5 h-3.5 text-[#D4AF37]" /> : <Sun className="w-3.5 h-3.5 text-[#D4AF37]" />}
                      {sessionType === 'night' ? 'Session Nuit' : 'Day Use'}
                    </span>
                    <span className="text-[#F5F2EB]">{sessionType === 'night' ? `${property.night_check_in_hour || '18:00'} → ${property.night_check_out_hour || '10:00'}` : `${property.dayuse_start_hour || '12:00'} → ${property.dayuse_end_hour || '15:00'}`}</span>
                  </li>
                  {checkIn && (
                    <li className="flex justify-between">
                      <span className="text-[#A39E93]">{sessionType === 'night' ? 'Arrivée' : 'Date'}</span>
                      <span className="text-[#F5F2EB]">{format(parseISO(checkIn), 'd MMM yyyy', { locale: fr })}</span>
                    </li>
                  )}
                  {sessionType === 'night' && checkOut && (
                    <li className="flex justify-between">
                      <span className="text-[#A39E93]">Départ</span>
                      <span className="text-[#F5F2EB]">{format(parseISO(checkOut), 'd MMM yyyy', { locale: fr })}</span>
                    </li>
                  )}
                  <li className="flex justify-between">
                    <span className="text-[#A39E93]">Voyageurs</span>
                    <span className="text-[#F5F2EB]">{guestsCount}</span>
                  </li>
                </ul>

                {pricing && (
                  <>
                    <div className="luxury-divider my-6" />
                    <ul className="space-y-3 text-sm font-body font-light">
                      <li className="flex justify-between">
                        <span className="text-[#A39E93]">{sessionType === 'night' ? `${pricing.nights} × €${pricing.base_price}` : 'Day Use'}</span>
                        <span className="text-[#F5F2EB] tabular-nums">€{pricing.units_total?.toFixed(2)}</span>
                      </li>
                      {pricing.cleaning_fee > 0 && (
                        <li className="flex justify-between">
                          <span className="text-[#A39E93]">Frais de ménage</span>
                          <span className="text-[#F5F2EB] tabular-nums">€{pricing.cleaning_fee?.toFixed(2)}</span>
                        </li>
                      )}
                      {pricing.selected_options?.map(o => (
                        <li key={o.id} className="flex justify-between">
                          <span className="text-[#A39E93]">{o.name}</span>
                          <span className="text-[#F5F2EB] tabular-nums">€{o.price?.toFixed(2)}</span>
                        </li>
                      ))}
                      {pricing.discount > 0 && (
                        <li className="flex justify-between text-[#D4AF37]">
                          <span>Promo {pricing.promo?.code}</span>
                          <span className="tabular-nums">-€{pricing.discount?.toFixed(2)}</span>
                        </li>
                      )}
                    </ul>
                    <div className="luxury-divider my-6" />
                    <div className="flex items-baseline justify-between">
                      <span className="overline">Total</span>
                      <span className="font-display text-4xl text-[#D4AF37] tabular-nums">€{pricing.total_price?.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="mt-6 space-y-2 text-xs text-[#5C5851] font-body font-light">
              <p className="flex items-center gap-2"><Star className="w-3 h-3 text-[#D4AF37]" /> Confirmation immédiate</p>
              <p className="flex items-center gap-2"><Shield className="w-3 h-3 text-[#D4AF37]" /> Paiement sécurisé</p>
              <p className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#D4AF37]" /> Modification jusqu'à 48h avant</p>
            </div>
          </div>
        </aside>
      </div>

      {/* Sticky bottom CTA bar on mobile (with total + next action) */}
      {pricing && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#0D0C0B]/95 backdrop-blur border-t border-[#D4AF37]/30 px-4 py-3 flex items-center justify-between gap-4" data-testid="mobile-sticky-cta">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#A39E93] font-body">Total</p>
            <p className="font-display text-2xl text-[#D4AF37] tabular-nums">€{pricing.total_price?.toFixed(2)}</p>
          </div>
          {step < 3 && (
            <button onClick={step === 1 ? goNextFromStay : goNextFromInfo} disabled={checking || submitting} className="btn-luxury px-5 py-3 text-[10px]">
              {checking || submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continuer <ArrowRight className="ml-2 w-3 h-3" /></>}
            </button>
          )}
          {step === 3 && paymentMethod !== 'paypal' && (
            <button onClick={handleConfirm} disabled={submitting} className="btn-luxury px-5 py-3 text-[10px]">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
            </button>
          )}
        </div>
      )}

      <FloatingContact />
    </div>
  );
}
